import { Box, Line } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { MeasureKind, UnitsByMeasure } from 'convert';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import { WindowProps } from '../../@types/Components';
import { EInteractionMode, ESubMenu, EUnitType } from '../../@types/Enums';
import useDraggableMesh from '../../hooks/useDraggableMesh';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { INTERNAL_TO_CM_FACTOR } from '../../utils/constants';
import { convertTo } from '../../utils/conversionUtils';
import RENDER_ORDERS from '../../utils/renderOrder';
import { snapWindowToWall } from '../../utils/wallElementSnapping';
import WindowLengthIndicator from './indicators/WindowLengthIndicator';

export { snapWindowToWall };

const Window = ({ wallModel, onWallUpdate, window, onClick, selectedWindow, wallIndex, windowIndex }: WindowProps): React.JSX.Element => {
  const { length: width, angle } = window;
  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState<boolean>(false);
  const { interactionMode, setCurrentlyActiveSubMenu } = useToolbarContext();
  const { showWindowLength } = useMultiSelectToggleContext();
  const { setWindowWidthAttribute } = useBuilding();
  const [windowWidth, setWindowWidth] = useState<number>(width);
  const { unitType } = useUnitContext();

  const widthCm = window.length * INTERNAL_TO_CM_FACTOR;
  const widthValue =
    unitType === EUnitType.METRIC
      ? parseFloat(widthCm.toFixed(3))
      : Math.round(convertTo(widthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
  const widthUnit = unitType === EUnitType.METRIC ? 'cm' : 'in';

  useEffect(() => {
    setWindowWidthAttribute(wallIndex, windowIndex, widthValue, widthUnit);
  }, [setWindowWidthAttribute, wallIndex, windowIndex, widthValue, widthUnit]);

  useEffect(() => {
    setWindowWidth(width);
  }, [width]);

  const meshColor = useMemo(
    () => (selectedWindow.wallIndex !== undefined && selectedWindow.windowIndex !== undefined ? '#009fe3' : hovered ? '#888888' : 'white'),
    [hovered, selectedWindow],
  );

  const constraintFunction = useCallback((newPosition: THREE.Vector3): THREE.Vector3 => snapWindowToWall(wallModel, newPosition, width), [wallModel, width]);

  const updateWindowPosition = useCallback(
    (newPosition: THREE.Vector3): void => {
      window.position = newPosition;
      onWallUpdate(wallModel.uuid, wallModel, EHistoryAction.WINDOW_POSITION_CHANGED);
    },
    [onWallUpdate, wallModel, window],
  );

  useDraggableMesh(
    groupRef,
    constraintFunction,
    updateWindowPosition,
    interactionMode !== EInteractionMode.MOVE,
    interactionMode !== EInteractionMode.MOVE,
  );

  const windowBoxOutlinePoints = useMemo(() => {
    const hw = windowWidth / 2;
    const hd = wallModel.depth / 2;
    return [
      new THREE.Vector3(-hw, hd, 0.01),
      new THREE.Vector3(hw, hd, 0.01),
      new THREE.Vector3(hw, -hd, 0.01),
      new THREE.Vector3(-hw, -hd, 0.01),
      new THREE.Vector3(-hw, hd, 0.01),
    ];
  }, [windowWidth, wallModel.depth]);

  const glassLines = useMemo(() => {
    const hw = windowWidth / 2;
    return [
      [new THREE.Vector3(-hw, 0, 0.02), new THREE.Vector3(hw, 0, 0.02)],
    ];
  }, [windowWidth]);

  const handlePointerEvents = (event: ThreeEvent<PointerEvent>, callback: (e: ThreeEvent<PointerEvent>) => void): void => {
    event.stopPropagation();
    setCurrentlyActiveSubMenu(ESubMenu.NONE);
    if (interactionMode !== EInteractionMode.MOVE) {
      callback && callback(event);
    }
  };

  return (
    <group name={`group-window#${selectedWindow.windowIndex}-wall#${selectedWindow.wallIndex}`} ref={groupRef} position={window.position} rotation={[0, 0, angle]}>
      {(hovered || (selectedWindow && selectedWindow.wallIndex !== undefined && selectedWindow.windowIndex !== undefined) || showWindowLength) && (
        <WindowLengthIndicator
          windowWidth={windowWidth}
          rotation={angle}
          color={'#444444'}
        />
      )}
      <group position={[0, 0, 0.05]}>
        <Box
          name={'window-box'}
          args={[windowWidth, wallModel.depth, 0]}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            handlePointerEvents(e, onClick);
          }}
          onPointerEnter={(e) => handlePointerEvents(e, () => setHovered(true))}
          onPointerLeave={(e) => handlePointerEvents(e, () => setHovered(false))}
          renderOrder={RENDER_ORDERS.window} 
        >
          <meshBasicMaterial attach="material" color={meshColor} transparent={true} opacity={1} />
        </Box>

        <Line 
          points={windowBoxOutlinePoints} 
          color="black" 
          lineWidth={1.8} 
          renderOrder={RENDER_ORDERS.window + 5} 
        />

        {glassLines.map((pts, i) => (
          <Line 
            key={i} 
            points={pts} 
            color="#444444" 
            lineWidth={2} 
            renderOrder={RENDER_ORDERS.window + 10} 
          />
        ))}
      </group>
    </group>
  );
};

export default memo(Window);
